import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import moment from 'moment-timezone';
import { Button } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

import DATE_FORMATS from '../../../utilities/DateTimeFormats';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import { MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFindById } from '../../../services/FormQueryService';
import useSession, { useUser } from '../../../stores/SessionStore';
import format, { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { NgpLabelValue } from '../../form/NgpLabelValue';
import {
    doPasswordsMatch,
    PASSWORD_MISMATCH_ERROR_TEXT,
    shouldDisableEmail,
    shouldDisableName,
    shouldDisableStatusAndRole,
    shouldDisableTimeZone,
    shouldDisableUsername,
    shouldShowPassword,
    shouldShowSourceField,
} from './UserAdminHelper';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { useFormDeleteService, useFormDeleteWrapper } from '../../../services/FormDeleteService';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { throwValidationError, useDataService } from '../../../services/ApiDataService';
import useUiAlerts, { createAlertConfig, ERROR, INFO } from '../../../stores/AlertsStore';
import { getSystemTime } from '../../../stores/SystemTimeStore';
import { dateTimeFormatter } from '../../../utilities';
import {
    CONFIRM_PASSWORD_FIELD,
    DARK_MODE_FIELD,
    MERIDIEM_TIME_FIELD,
    DETAIL_PASSWORD,
    EMAIL_FIELD,
    FIRST_NAME_FIELD,
    LAST_NAME_FIELD,
    LOCKED_OUT_UNTIL_FIELD,
    PASSWORD_FIELD,
    TIME_ZONE_FIELD,
    USER_NAME_FIELD,
    USER_ROLE,
    USER_ROLE_FIELD,
    USER_SOURCE_NG1,
    USER_STATUS_ENABLED_OPTION,
    USER_STATUS_FIELD,
    GROUPS_FIELD,
} from './UserConstants';
import NgpToggle from '../../form/NgpToggle';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import UserDetailSection from './UserDetailSection';
import UserPasswordSection from './UserPasswordSection';
import PlmUserAccess from './PlmUserAccess';
import { UserAdminGroupsSection } from './UserAdminGroupsSection';
import { DEFAULT_GROUP_UUID } from '../groups/GroupsAdminConstants';

const functionName = AdminPages.Users;
const goToUrl = '..';
const baseUrl = '/ipm/v1/admin/users/';
const defaultValues = {
    [USER_ROLE_FIELD]: USER_ROLE,
    [USER_STATUS_FIELD]: USER_STATUS_ENABLED_OPTION,
    [FIRST_NAME_FIELD]: '',
    [LAST_NAME_FIELD]: '',
    [TIME_ZONE_FIELD]: moment.tz.guess(),
    [EMAIL_FIELD]: '',
    [USER_NAME_FIELD]: '',
    [PASSWORD_FIELD]: '',
    [CONFIRM_PASSWORD_FIELD]: '',
    [GROUPS_FIELD]: [DEFAULT_GROUP_UUID],
};

export default function UserAdminDetail() {
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { deleteWrapper, deleting } = useFormDeleteWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, getValues, setError, handleSubmit } = methods;
    const [groupsSelectionIsDirty, setGroupsSelectionIsDirty] = useState(false);
    const functionBreadCrumbs = [{ link: goToUrl, text: i18n._(t`Users`) }];
    const newBreadCrumbText = t`New User`;

    const [, uiAlertActions] = useUiAlerts();
    const [loggedInUser] = useUser();
    const [, sessionActions] = useSession();
    const [showSourceField, setShowSourceField] = useState(false);
    const [disableStatusAndRole, setDisableStatusAndRole] = useState(false);
    const [disableName, setDisableName] = useState(false);
    const [disableUsername, setDisableUsername] = useState(false);
    const [disableTimeZone, setDisableTimeZone] = useState(false);
    const [disableEmail, setDisableEmail] = useState(false);
    const [showPassword, setShowPassword] = useState(true);
    const [showDarkModeToggle, setShowDarkModeToggle] = useState(false);
    const [showMeridiemTimeToggle, setShowMeridiemTimeToggle] = useState(false);
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const [lockoutMessage, setLockoutMessage] = useState(null);
    const formDeleteService = useFormDeleteService();
    const formSaveService = useFormSaveService();
    const dataService = useDataService();
    const [, setRulesSelectionIsDirty] = useState(false);

    /**
     * Post handling of data coming back from the server
     * @param response
     * @returns {*}
     */
    const postFindByIdHandler = (response) => {
        return dataService.get(`${baseUrl}/count`).then((countResponse) => {
            response.password = DETAIL_PASSWORD;
            response.confirmPassword = DETAIL_PASSWORD;
            if (_.isEmpty(response[TIME_ZONE_FIELD])) {
                response[TIME_ZONE_FIELD] = defaultValues[TIME_ZONE_FIELD];
            }

            setShowSourceField(shouldShowSourceField(response));
            setDisableStatusAndRole(shouldDisableStatusAndRole(response, countResponse.total));
            setDisableName(shouldDisableName(response));
            setDisableUsername(shouldDisableUsername(response));
            setDisableTimeZone(shouldDisableTimeZone(response));
            setDisableEmail(shouldDisableEmail(response));
            setShowPassword(shouldShowPassword(response));
            setShowDarkModeToggle(loggedInUser._id === response._id);
            setShowMeridiemTimeToggle(loggedInUser._id === response._id);
            setShowDeleteButton(loggedInUser._id !== response._id);

            if (response[LOCKED_OUT_UNTIL_FIELD] && moment(response[LOCKED_OUT_UNTIL_FIELD]).isAfter(getSystemTime())) {
                const formattedLockoutDate = dateTimeFormatter(response[LOCKED_OUT_UNTIL_FIELD], {
                    toFormat: DATE_FORMATS.TIME_SEC_12H_TZ,
                });
                setLockoutMessage(
                    i18n._(
                        t`This user is locked out until ${formattedLockoutDate}.  Change the user's password and click Save to unlock this user.`
                    )
                );
            }

            if (response.source === USER_SOURCE_NG1) {
                uiAlertActions.createAlert(
                    createAlertConfig(
                        INFO,
                        i18n._(
                            t`User ${response[USER_NAME_FIELD]} is managed by nGeniusOne. Most of ${response[USER_NAME_FIELD]}'s information can only be edited in nGeniusOne.`
                        )
                    )
                );
            }

            return response;
        });
    };

    const findByIdErrorHandler = (errorObject) => {
        const noPermissionError = 'user_detail_not_allowed';
        if (errorObject?.responseBody?.message === noPermissionError) {
            return createAlertConfig(ERROR, i18n._(t`You do not have permission to modify this user.`));
        }

        return null;
    };

    const { initialized, finding, formData } = useFindById({
        url: baseUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper(findByIdErrorHandler, goToUrl),
    });
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, recordId, formData, EMAIL_FIELD, newBreadCrumbText);

    const handleFormSubmit = (dataToSave = {}) => {
        if (!doPasswordsMatch(getValues(PASSWORD_FIELD), getValues(CONFIRM_PASSWORD_FIELD))) {
            setError(CONFIRM_PASSWORD_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: PASSWORD_MISMATCH_ERROR_TEXT,
            });
            return throwValidationError();
        }

        return formSaveService
            .save({
                url: baseUrl,
                recordId: formData._id,
                dataToSave: _.extend(dataToSave, { _id: formData._id }),
                setError,
            })
            .then((response) => {
                if (showDarkModeToggle) {
                    sessionActions.setDarkMode(dataToSave.darkMode);
                }

                if (showMeridiemTimeToggle) {
                    sessionActions.setUser(response);
                }

                return response;
            });
    };

    const handleConfirmedDelete = () => {
        return deleteWrapper({
            deleteFunction: () => formDeleteService.deleteById(baseUrl, recordId),
            onDeleteGoTo: goToUrl,
        })();
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, goToUrl);

    const dirtyCheck = (isDirty) => isDirty || groupsSelectionIsDirty;

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    {showDeleteButton && (
                        <ConfirmDeleteButton handleConfirmedDelete={handleConfirmedDelete} plural={false} />
                    )}
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized}
                    loading={finding || saving || deleting}
                    dirtyCheck={dirtyCheck}
                >
                    <FormSection title={<Trans>User Detail</Trans>}>
                        {lockoutMessage && <Typography>{lockoutMessage}</Typography>}

                        <UserDetailSection
                            showSourceField={showSourceField}
                            disableStatusAndRole={disableStatusAndRole}
                            disableName={disableName}
                            disableEmail={disableEmail}
                            disableUsername={disableUsername}
                            disableTimeZone={disableTimeZone}
                        />

                        {showPassword && <UserPasswordSection showPassword={showPassword} />}

                        {showDarkModeToggle && (
                            <NgpToggle fieldName={DARK_MODE_FIELD} label={<Trans>Use Dark Mode</Trans>} />
                        )}

                        {showMeridiemTimeToggle && (
                            <NgpToggle
                                fieldName={MERIDIEM_TIME_FIELD}
                                inverted
                                label={<Trans>24 hour time display</Trans>}
                            />
                        )}

                        {formData.lastLoginSuccess && (
                            <NgpLabelValue
                                dataLabel="Last successful login"
                                dataValue={format(formData.lastLoginSuccess, { type: FORMATTER_TYPES.DATETIME })}
                            />
                        )}

                        {formData.lastLoginFail && (
                            <NgpLabelValue
                                dataLabel="Last failed login"
                                dataValue={format(formData.lastLoginFail, { type: FORMATTER_TYPES.DATETIME })}
                            />
                        )}
                    </FormSection>
                    <PlmUserAccess formData={formData} setSelectionIsDirty={setRulesSelectionIsDirty} />
                    <UserAdminGroupsSection formData={formData} setGroupsSelectionIsDirty={setGroupsSelectionIsDirty} />
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
